
import React, { useState, useRef } from 'react';
import { ProjectData } from '../types';
import { Building2, MapPin, Zap, Calendar, Lightbulb, User, Map, FileText, ArrowRight, Upload, Info, Layers, Tag, X } from 'lucide-react';

interface Props {
  onSubmit: (data: ProjectData) => void;
  isLoading: boolean;
}

export const InitForm: React.FC<Props> = ({ onSubmit, isLoading }) => {
  const [showForm, setShowForm] = useState(false);
  const [data, setData] = useState<ProjectData>({
    projectName: '',
    location: '',
    scale: '',
    standardFloorArea: '',
    ceilingHeight: '',
    utilitiesType: 'commercial',
    propertyFee: '',
    openingDate: '',
    customInfo: '',
    marketDataFile: null
  });
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(data);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // Convert FileList to Array
      const filesArray = Array.from(e.target.files);
      setData({ ...data, marketDataFile: filesArray });
    }
  };

  const removeFiles = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the parent click
    setData({ ...data, marketDataFile: null });
    if (fileInputRef.current) {
        fileInputRef.current.value = "";
    }
  };

  if (!showForm) {
    return (
      <div className="max-w-3xl mx-auto mt-6 md:mt-10 p-5 md:p-8 bg-white rounded-2xl shadow-xl border border-slate-100">
         <div className="text-center mb-6 md:mb-10">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-slate-900 rounded-full mx-auto flex items-center justify-center mb-4 shadow-lg">
                <Lightbulb className="w-8 h-8 md:w-10 md:h-10 text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">长租公寓资产策划顾问</h1>
            <p className="text-slate-500 mt-2 font-medium tracking-wide text-sm md:text-base">Asset Manager Pro</p>
         </div>

         <div className="space-y-4 md:space-y-6 text-slate-700 leading-relaxed animate-fade-in-up">
            <div className="bg-slate-50 p-4 md:p-6 rounded-xl border border-slate-200 shadow-sm">
                <h3 className="font-bold text-slate-900 mb-2 md:mb-3 flex items-center text-base md:text-lg">
                    <User className="w-5 h-5 md:w-6 md:h-6 mr-2 text-blue-600"/> 
                    我是谁？
                </h3>
                <p className="text-slate-600 text-sm md:text-base">
                    我是您的<strong>首席长租公寓资产管理专家 (Asset Manager)</strong>。致力于为用户提供专业的产品规划和投前测算。
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 md:p-5 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition">
                    <h3 className="font-bold text-slate-900 mb-2 md:mb-3 flex items-center text-base md:text-lg">
                        <Map className="w-5 h-5 md:w-6 md:h-6 mr-2 text-blue-600"/>
                        八步工作流
                    </h3>
                    <ul className="text-xs md:text-sm space-y-3 list-none text-slate-600">
                        {/* Increased width to w-20 to prevent wrapping */}
                        <li className="flex items-center"><span className="font-mono font-bold text-blue-600 mr-2 w-20 text-right whitespace-nowrap flex-shrink-0">Step 1.</span> 市场及竞品侦察</li>
                        <li className="flex items-center"><span className="font-mono font-bold text-blue-600 mr-2 w-20 text-right whitespace-nowrap flex-shrink-0">Step 2.</span> 客群画像与引力法则</li>
                        <li className="flex items-center"><span className="font-mono font-bold text-blue-600 mr-2 w-20 text-right whitespace-nowrap flex-shrink-0">Step 3.</span> 双轨博弈定价</li>
                        <li className="flex items-center"><span className="font-mono font-bold text-blue-600 mr-2 w-20 text-right whitespace-nowrap flex-shrink-0">Step 4.</span> 产品策略与坪效</li>
                        <li className="flex items-center"><span className="font-mono font-bold text-blue-600 mr-2 w-20 text-right whitespace-nowrap flex-shrink-0">Step 5.</span> 强排设计任务书</li>
                        <li className="flex items-center"><span className="font-mono font-bold text-orange-500 mr-2 w-20 text-right whitespace-nowrap flex-shrink-0">Step 5.5</span> 方案博弈与重定价</li>
                        <li className="flex items-center"><span className="font-mono font-bold text-blue-600 mr-2 w-20 text-right whitespace-nowrap flex-shrink-0">Step 6.</span> 经营测算及投资回报</li>
                        <li className="flex items-center"><span className="font-mono font-bold text-blue-600 mr-2 w-20 text-right whitespace-nowrap flex-shrink-0">Step 7.</span> 总结备忘</li>
                    </ul>
                </div>
                <div className="bg-slate-50 p-4 md:p-5 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition">
                    <h3 className="font-bold text-slate-900 mb-2 md:mb-3 flex items-center text-base md:text-lg">
                        <FileText className="w-5 h-5 md:w-6 md:h-6 mr-2 text-blue-600"/>
                        核心交付物
                    </h3>
                    <ul className="text-xs md:text-sm space-y-2.5 list-none text-slate-600">
                        <li className="flex items-start"><span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2 mt-1.5 flex-shrink-0"></span><span><strong>建筑强排任务书</strong> (Markdown)</span></li>
                        <li className="flex items-start"><span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2 mt-1.5 flex-shrink-0"></span><span><strong>投资测算神表</strong> (GOP/IRR)</span></li>
                        <li className="flex items-start"><span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2 mt-1.5 flex-shrink-0"></span><span><strong>法医级深度研报</strong> (HTML)</span></li>
                    </ul>
                </div>
            </div>
         </div>

         <button 
            onClick={() => setShowForm(true)}
            className="w-full mt-8 md:mt-10 bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 md:py-4 rounded-xl shadow-xl transition transform hover:scale-[1.01] flex items-center justify-center text-base md:text-lg"
         >
            启动新项目策划
            <ArrowRight className="ml-3 w-4 h-4 md:w-5 md:h-5" />
         </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto bg-white p-5 md:p-8 rounded-xl shadow-lg border border-gray-100 mt-6 md:mt-10 animate-fade-in-up">
      <div className="mb-6 md:mb-8 text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">项目参数初始化</h1>
        <p className="text-sm md:text-base text-gray-500">请输入关键基础数据以启动 AI 引擎</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
        
        {/* Project Name */}
        <div>
          <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
            <Tag className="w-4 h-4 mr-2" /> 项目名称 / 代号
          </label>
          <input
            required
            type="text"
            placeholder="例如：坂田华为基地店 (Project Alpha)"
            className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-sm md:text-base"
            value={data.projectName}
            onChange={(e) => setData({ ...data, projectName: e.target.value })}
          />
          <p className="text-xs text-gray-400 mt-1">报告中将统一使用此名称指代项目</p>
        </div>

        <div>
          <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
            <MapPin className="w-4 h-4 mr-2" /> 项目核心坐标
          </label>
          <input
            required
            type="text"
            placeholder="例如：深圳市龙岗区坂田街道5号线坂田站A口"
            className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-sm md:text-base"
            value={data.location}
            onChange={(e) => setData({ ...data, location: e.target.value })}
          />
          <p className="text-xs text-gray-400 mt-1">越精确越好，最好包含地铁站名</p>
        </div>

        <div>
            <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                <Info className="w-4 h-4 mr-2" /> 个性化信息 / 已知对标 (非必填)
            </label>
            <textarea
              placeholder="请输入您已知的周边竞品信息（如：对标万科云城泊寓，租金约3000），或其他特殊项目背景。AI将重点参考此信息。"
              className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 h-20 md:h-24 text-sm"
              value={data.customInfo}
              onChange={(e) => setData({ ...data, customInfo: e.target.value })}
            />
        </div>

        <div>
            <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                <Upload className="w-4 h-4 mr-2" /> 竞品调研表 / 市场数据 (强烈推荐)
            </label>
            <div 
                className={`border-2 border-dashed rounded-lg p-4 md:p-6 text-center transition relative ${data.marketDataFile && data.marketDataFile.length > 0 ? 'bg-green-50 border-green-200' : 'border-gray-300 hover:bg-gray-50 cursor-pointer'}`}
                onClick={() => !data.marketDataFile && fileInputRef.current?.click()}
            >
                <input 
                    type="file" 
                    multiple
                    ref={fileInputRef}
                    className="hidden"
                    onChange={handleFileChange}
                    accept=".xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg"
                />
                
                {data.marketDataFile && data.marketDataFile.length > 0 ? (
                    <div className="flex flex-col items-center justify-center relative z-10">
                        <div className="flex items-center mb-1 text-green-700 font-semibold">
                            <FileText className="w-5 h-5 mr-2" />
                            已选择 {data.marketDataFile.length} 个文件
                        </div>
                        <span className="text-xs text-green-600 mb-3">
                            {data.marketDataFile.map(f => f.name).join(', ').substring(0, 30)}...
                        </span>
                        <button 
                            onClick={removeFiles}
                            type="button"
                            className="px-3 py-1 bg-white border border-green-300 text-green-700 text-xs rounded-full hover:bg-green-100 flex items-center shadow-sm"
                        >
                            <X className="w-3 h-3 mr-1" />
                            清除文件
                        </button>
                    </div>
                ) : (
                    <div className="text-gray-500">
                        <p className="font-medium text-blue-600 text-sm md:text-base">点击上传文件 (支持多选)</p>
                        <p className="text-xs mt-1">支持 Excel/CSV/图片/PDF。若无数据，AI将自动检索。</p>
                    </div>
                )}
            </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div>
            <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
              <Building2 className="w-4 h-4 mr-2" /> 项目体量 (总面积/总间数)
            </label>
            <input
              required
              type="text"
              placeholder="例如：5000 (默认单位：平方米)"
              className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              value={data.scale}
              onChange={(e) => setData({ ...data, scale: e.target.value })}
            />
          </div>
          <div>
            <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
              <Layers className="w-4 h-4 mr-2" /> 标准层面积 (选填)
            </label>
            <input
              type="text"
              placeholder="例如：800 (默认单位：平方米)"
              className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              value={data.standardFloorArea}
              onChange={(e) => setData({ ...data, standardFloorArea: e.target.value })}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
           <div>
            <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
              <Building2 className="w-4 h-4 mr-2" /> 建筑层高
            </label>
            <input
              required
              type="text"
              placeholder="例如：4.5 (默认单位：米)"
              className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              value={data.ceilingHeight}
              onChange={(e) => setData({ ...data, ceilingHeight: e.target.value })}
            />
          </div>
          <div>
            <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
              <Calendar className="w-4 h-4 mr-2" /> 预计开业时间
            </label>
            <input
              required
              type="month"
              className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              value={data.openingDate}
              onChange={(e) => setData({ ...data, openingDate: e.target.value })}
            />
            <p className="text-xs text-gray-400 mt-1">决定爬坡曲线与季节性修正</p>
          </div>
        </div>

        <div>
            <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
              <Zap className="w-4 h-4 mr-2" /> 水电与物业
            </label>
            <div className="flex space-x-4 mb-3">
               <label className="flex items-center space-x-2 cursor-pointer">
                 <input type="radio" name="utility" checked={data.utilitiesType === 'commercial'} onChange={() => setData({...data, utilitiesType: 'commercial'})} className="text-blue-600 focus:ring-blue-500" />
                 <span className="text-sm text-gray-600">商水商电</span>
               </label>
               <label className="flex items-center space-x-2 cursor-pointer">
                 <input type="radio" name="utility" checked={data.utilitiesType === 'residential'} onChange={() => setData({...data, utilitiesType: 'residential'})} className="text-blue-600 focus:ring-blue-500" />
                 <span className="text-sm text-gray-600">民水民电</span>
               </label>
            </div>
            <input
              type="text"
              placeholder="物业费预算 (元/平/月)"
              className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              value={data.propertyFee}
              onChange={(e) => setData({ ...data, propertyFee: e.target.value })}
            />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-slate-900 text-white py-3 md:py-4 rounded-lg font-bold hover:bg-slate-800 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center text-base md:text-lg"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              AI 正在分析竞品数据...
            </>
          ) : (
            "立即初始化项目"
          )}
        </button>
      </form>
    </div>
  );
};
